import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Package, MapPin, Phone, Mail, Facebook, Instagram, Twitter, Youtube, ArrowRight, Clock, Shield, Heart, MessageCircle, Sparkles } from 'lucide-react';
import api from '../../lib/api';

export const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  
  const [contactInfo, setContactInfo] = useState({
    support_email: 'liantiaoja@gmail.com',
    support_phone: '+1 876-805-1013',
    whatsapp_number: '18768051013',
    business_hours: '24/7 Customer Support',
    address: 'Kingston, Jamaica'
  });
  
  const [socialMedia, setSocialMedia] = useState({
    facebook: 'https://facebook.com/liantiaoja',
    instagram: 'https://instagram.com/liantiaoja',
    twitter: 'https://twitter.com/liantiaoja',
    youtube: ''
  });
  
  const [showLegalBanner, setShowLegalBanner] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const [contactRes, socialRes, featuresRes] = await Promise.all([
        api.get('/site-config/contact').catch(() => ({ data: contactInfo })),
        api.get('/site-config/social-media').catch(() => ({ data: socialMedia })),
        api.get('/site-config/new-features').catch(() => ({ data: {} }))
      ]);
      
      if (contactRes.data) setContactInfo(contactRes.data);
      if (socialRes.data) setSocialMedia(socialRes.data);
      
      // Check if legal section banner should show (120 days from feature launch)
      const launchDate = new Date('2026-02-10');
      const daysElapsed = Math.floor((new Date() - launchDate) / (1000 * 60 * 60 * 24));
      setShowLegalBanner(daysElapsed < 120);
    } catch (error) {
      console.error('Error fetching footer config:', error);
    }
  };

  const footerLinks = {
    services: [
      { label: 'Shop Deals', href: '/shop' },
      { label: 'Sell Products', href: '/seller/apply' },
      { label: 'Package Delivery', href: '/send' },
      { label: 'PUDO Centres', href: '/pudo' },
      { label: 'Track Order', href: '/tracking' },
    ],
    partners: [
      { label: 'Become a PPV Driver', href: '/ppv/apply' },
      { label: 'Become a PUDO Centre', href: '/pudo/apply' },
      { label: 'Sell on Liantiao', href: '/seller/apply' },
      { label: 'Partner Stories', href: '/partners' },
    ],
    support: [
      { label: 'Help Center', href: '/help' },
      { label: 'Contact Us', href: '/contact' },
      { label: 'FAQs', href: '/faqs' },
      { label: 'Returns', href: '/returns' },
    ],
    legal: [
      { label: 'Terms of Service', href: '/terms' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Seller Agreement', href: '/seller-agreement' },
      { label: 'Driver Agreement', href: '/driver-agreement' },
    ],
  };

  const socialIcons = {
    facebook: Facebook,
    instagram: Instagram,
    twitter: Twitter,
    youtube: Youtube
  };

  const handleWhatsApp = () => {
    const number = contactInfo.whatsapp_number?.replace(/[^0-9]/g, '');
    if (number) {
      window.open(`https://wa.me/${number}`, '_blank');
    }
  };

  return (
    <footer id="contact" className="bg-foreground text-background relative overflow-hidden">
      <div className="absolute inset-0 pattern-grid opacity-5" />
      
      {/* Built by Jamaicans Banner */}
      <div className="bg-gradient-jamaica py-4">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-center gap-3">
            <Heart className="w-5 h-5 text-primary-foreground fill-primary-foreground animate-pulse" />
            <span className="font-display text-xl text-primary-foreground tracking-wide">BUILT BY JAMAICANS • POWERING JAMAICA</span>
            <span className="text-2xl">🇯🇲</span>
          </div>
        </div>
      </div>
      
      <div className="border-b border-background/10">
        <div className="container mx-auto px-4 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="font-display text-2xl lg:text-3xl text-background mb-2">STAY UPDATED WITH <span className="text-secondary">LIANTIAO</span></h3>
              <p className="text-background/70">Get the latest updates, offers, and delivery tips straight to your inbox.</p>
            </div>
            <div className="flex w-full lg:w-auto gap-3">
              <Input type="email" placeholder="Enter your email" className="h-12 w-full lg:w-80 bg-background/10 border-background/20 text-background placeholder:text-background/50 focus:border-secondary" />
              <Button variant="gold" size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">Subscribe<ArrowRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 py-16 relative">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-jamaica flex items-center justify-center shadow-lg"><Package className="w-6 h-6 text-primary-foreground" /></div>
              <div><span className="font-display text-2xl text-background">LIANTIAO</span><p className="text-xs text-background/60">Jamaica Delivery</p></div>
            </div>
            <p className="text-background/70 mb-6 leading-relaxed">Jamaica's marketplace for great deals and local products. Shop, sell, and get fast delivery across Kingston, St. Thomas, St. Catherine, Clarendon, St. Ann & Falmouth. More parishes coming soon!</p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-background/80">
                <Phone className="w-4 h-4 text-secondary" />
                <span>{contactInfo.support_phone}</span>
              </div>
              <div className="flex items-center gap-3 text-background/80">
                <Mail className="w-4 h-4 text-secondary" />
                <span>{contactInfo.support_email}</span>
              </div>
              <div 
                className="flex items-center gap-3 text-background/80 cursor-pointer hover:text-secondary transition-colors"
                onClick={handleWhatsApp}
              >
                <MessageCircle className="w-4 h-4 text-secondary" />
                <span>WhatsApp Us</span>
              </div>
              <div className="flex items-center gap-3 text-background/80">
                <Clock className="w-4 h-4 text-secondary" />
                <span>{contactInfo.business_hours}</span>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              {Object.entries(socialMedia).map(([key, url]) => {
                if (!url) return null;
                const IconComponent = socialIcons[key];
                if (!IconComponent) return null;
                return (
                  <a 
                    key={key} 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label={key} 
                    className="w-10 h-10 rounded-xl bg-background/10 flex items-center justify-center hover:bg-secondary hover:text-secondary-foreground transition-all duration-300"
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          <div><h4 className="font-semibold text-background mb-4">Services</h4><ul className="space-y-3">{footerLinks.services.map((link, index) => (<li key={index}><a href={link.href} className="text-background/70 hover:text-secondary transition-colors">{link.label}</a></li>))}</ul></div>
          <div><h4 className="font-semibold text-background mb-4">Partner With Us</h4><ul className="space-y-3">{footerLinks.partners.map((link, index) => (<li key={index}><a href={link.href} className="text-background/70 hover:text-secondary transition-colors">{link.label}</a></li>))}</ul></div>
          <div><h4 className="font-semibold text-background mb-4">Support</h4><ul className="space-y-3">{footerLinks.support.map((link, index) => (<li key={index}><a href={link.href} className="text-background/70 hover:text-secondary transition-colors">{link.label}</a></li>))}</ul></div>
          <div>
            <div className="flex items-center gap-2 mb-4">
              <h4 className="font-semibold text-background">Legal</h4>
              {showLegalBanner && (
                <Badge className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-2 py-0">
                  <Sparkles className="w-3 h-3 mr-1" /> NEW
                </Badge>
              )}
            </div>
            <ul className="space-y-3">{footerLinks.legal.map((link, index) => (<li key={index}><a href={link.href} className="text-background/70 hover:text-secondary transition-colors">{link.label}</a></li>))}</ul>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-8 mt-12 pt-12 border-t border-background/10">
          <div className="flex items-center gap-2 text-background/60"><Shield className="w-5 h-5 text-secondary" /><span className="text-sm">Secure Payments</span></div>
          <div className="flex items-center gap-2 text-background/60"><MapPin className="w-5 h-5 text-secondary" /><span className="text-sm">6 Parishes Active</span></div>
          <div className="flex items-center gap-2 text-background/60"><Package className="w-5 h-5 text-secondary" /><span className="text-sm">Fast Delivery</span></div>
          <div className="flex items-center gap-2 text-background/60"><Clock className="w-5 h-5 text-secondary" /><span className="text-sm">Daily Specials</span></div>
        </div>
      </div>

      <div className="border-t border-background/10">
        <div className="container mx-auto px-4 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-background/60 text-sm text-center md:text-left">© {currentYear} Liantiao Jamaica. All rights reserved. <span className="text-secondary">Built with ❤️ in Jamaica</span></p>
            <div className="flex items-center gap-6">
              <span className="text-background/40 text-sm">Accepted Payments:</span>
              <div className="flex gap-2">{['Visa', 'MC', 'NCB', 'JN'].map((payment, index) => (<div key={index} className="px-3 py-1 rounded bg-background/10 text-background/60 text-xs font-medium">{payment}</div>))}</div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
